import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useFileUrl, uploadFile } from "../lib/files";
import { useAuth } from "../context/AuthContext";
import { MessageSquare, Send, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";

export default function Messages() {
  const { user } = useAuth();
  const [queries, setQueries] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [photoPath, setPhotoPath] = useState(null);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/queries");
    setQueries(data);
    if (active) {
      const t = await api.get(`/queries/${active}`);
      setThread(t.data);
    }
  }, [active]);

  useEffect(() => {
    load();
    const i = setInterval(load, 15000);
    return () => clearInterval(i);
  }, [load]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [thread]);

  const open = async (q) => {
    setActive(q.id);
    const { data } = await api.get(`/queries/${q.id}`);
    setThread(data);
  };

  const send = async () => {
    if (!text.trim() && !photoPath) return;
    await api.post(`/queries/${active}/messages`, {
      text: text || "(photo)",
      photo_path: photoPath,
    });
    setText("");
    setPhotoPath(null);
    const { data } = await api.get(`/queries/${active}`);
    setThread(data);
    load();
  };

  return (
    <div data-testid="messages-page" className="flex flex-col h-[calc(100vh-7rem)]">
      <div className="mb-4">
        <h1 className="font-chivo font-black text-3xl tracking-tight">Messages</h1>
        <p className="text-xs text-gray-500 mt-1">
          {user?.role === "admin" || user?.role === "staff"
            ? "All queries from the team — respond to any thread."
            : "Only conversations you've started or been invited to are visible."}
        </p>
      </div>

      <div className="flex-1 grid grid-cols-1 md:grid-cols-[300px_1fr] gap-4 min-h-0">
        <div className="bg-white border border-gray-200 overflow-y-auto">
          {queries.length === 0 && (
            <div className="p-4 text-sm text-gray-500">No conversations yet.</div>
          )}
          {queries.map((q) => (
            <button
              key={q.id}
              onClick={() => open(q)}
              data-testid={`query-list-${q.id}`}
              className={`w-full text-left px-3 py-3 border-b border-gray-100 hover:bg-gray-50 ${
                active === q.id ? "bg-gray-50 border-l-4 border-l-black" : ""
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <MessageSquare size={12} className="text-gray-500 shrink-0" />
                <div className="font-chivo font-bold text-sm truncate">{q.room_name}</div>
                <span className="text-[10px] font-mono text-gray-400 ml-auto">{q.message_count}</span>
              </div>
              <div className="text-xs text-gray-700 truncate">{q.subject}</div>
              <div className="text-[10px] font-mono text-gray-400 mt-1 truncate">
                {q.initiator_email} · {new Date(q.updated_at).toLocaleDateString()}
              </div>
            </button>
          ))}
        </div>

        <div className="bg-white border border-gray-200 flex flex-col min-h-0">
          {!thread ? (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-500">
              Select a conversation
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-gray-200">
                <div className="font-chivo font-black">{thread.query.room_name}</div>
                <div className="text-[10px] font-mono text-gray-500 uppercase">
                  Started by {thread.query.initiator_email}
                </div>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                {thread.messages.map((m) => (
                  <Bubble key={m.id} m={m} mine={m.user_id === user?.id} />
                ))}
              </div>
              <div className="border-t border-gray-200 p-3 flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Type a reply…"
                    data-testid="message-input"
                    className="flex-1 border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-black"
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
                  />
                  <PhotoButton onPicked={setPhotoPath} />
                  <button
                    onClick={send}
                    data-testid="message-send"
                    className="bg-black text-white px-3 py-2 hover:bg-gray-800"
                  >
                    <Send size={14} />
                  </button>
                </div>
                {photoPath && <PhotoChip path={photoPath} onRemove={() => setPhotoPath(null)} />}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Bubble({ m, mine }) {
  const url = useFileUrl(m.photo_path);
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[80%] ${mine ? "bg-black text-white" : "bg-gray-100 text-black"} px-3 py-2`}>
        <div className="text-[10px] font-mono uppercase tracking-wider opacity-70 mb-1">
          {m.user_email} · {m.user_role}
        </div>
        {m.photo_path && url && (
          <a href={url} target="_blank" rel="noreferrer" title="Open full image">
            <img src={url} alt="att" className="max-h-40 mb-2 border border-white/30" />
          </a>
        )}
        <div className="text-sm whitespace-pre-wrap">{m.text}</div>
        <div className="text-[10px] font-mono opacity-60 mt-1">
          {new Date(m.at).toLocaleString()}
        </div>
      </div>
    </div>
  );
}

function PhotoButton({ onPicked }) {
  const [busy, setBusy] = useState(false);
  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const r = await uploadFile(f);
      onPicked(r.path);
    } catch (err) {
      const reason = formatApiError(err.response?.data?.detail) || err.message || "Unknown upload error";
      toast.error(`Upload failed: ${reason}`);
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <label className={`px-3 py-2 border border-gray-300 cursor-pointer hover:border-black ${busy ? "opacity-50" : ""}`}>
      <ImageIcon size={14} />
      <input type="file" accept="image/*,.jpg,.jpeg,.png,.webp,.heic,.heif" className="hidden" onChange={onPick} data-testid="message-photo-input" />
    </label>
  );
}

function PhotoChip({ path, onRemove }) {
  const url = useFileUrl(path);
  return (
    <div className="flex items-center gap-2 text-xs">
      {url && (
        <a href={url} target="_blank" rel="noreferrer" title="Open full image">
          <img src={url} alt="att" className="h-12 border border-gray-300" />
        </a>
      )}
      <button onClick={onRemove} className="text-xs font-mono uppercase border border-gray-300 px-2 py-1 hover:border-black">
        <X size={12} className="inline" /> Remove
      </button>
    </div>
  );
}
